<template>
  <section class="py-10" style="background-color:#E24C3B">
    <div class="max-w-[1280px] mx-auto px-4">
      <!-- Vierkant vlak -->
      <div class="bg-[#F7941E] p-6 lg:p-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-0">
      <!-- Linker vlak: kop + tekst + 3 cirkels -->
      <div class="px-6 lg:px-8 py-8 flex flex-col justify-center">
        <h2 class="text-[32px] font-black italic uppercase text-black leading-tight mb-3">
          {{ t('contact.title') }}
        </h2>
        <p class="text-black/90 text-[15px] leading-relaxed max-w-[720px]">
          {{ t('contact.intro') }}
        </p>
        <div class="mt-6 flex items-center gap-4">
          <div v-for="(photo, idx) in photosToShow" :key="idx" class="w-16 h-16 rounded-full overflow-hidden border-2 border-black/30 bg-white">
            <img v-if="photo" :src="photo" :alt="t('aria.portrait')" class="w-full h-full object-cover" />
            <div v-else class="w-full h-full grid place-items-center text-black/50 text-sm">{{ t('labels.photo') }}</div>
          </div>
        </div>
      </div>

      <!-- Rechter vlak: formulier -->
      <div class="px-6 lg:px-8 py-8">
        <!-- Successtate -->
        <div v-if="submitted" class="bg-[#e7d7bf] p-5">
          <div class="flex items-center gap-3 mb-3">
            <div class="w-9 h-9 bg-black text-white grid place-items-center font-extrabold">✓</div>
            <h3 class="text-[18px] font-black italic uppercase">{{ t('contact.successTitle') }}</h3>
          </div>
          <p class="text-[13px] text-[#166534]">
            {{ t('contact.successBody') }}
          </p>
        </div>

        <!-- Formulier -->
        <form v-else @submit.prevent="handleSubmit" class="space-y-4" aria-live="polite">
          <!-- Jouw postcode -->
          <div>
            <input ref="refPostcode" v-model="postcode" type="text" :placeholder="t('contact.postcodePlaceholder')" @input="debouncedValidatePostcode" @blur="validatePostcode(true)"
                   class="w-full h-10 bg-white px-3 border text-[15px]" :class="postcodeClass" />
            <div v-if="postcodeMessage" :class="['mt-2 text-white px-3 py-2 text-[13px]', postcodeOk ? 'bg-[#3FA448]' : 'bg-[#b91c1c]']">
              {{ postcodeMessage }}
            </div>
          </div>

          <!-- Naam -->
          <div>
            <input ref="refName" v-model="name" type="text" :placeholder="t('contact.namePlaceholder')"
                   @blur="nameTouched=true" @input="nameTouched=true"
                   :class="['w-full h-10 bg-white px-3 border text-[15px]', (nameTouched && !name) ? 'border-[#b91c1c]' : 'border-[#d8d1c6]']"/>
          </div>

          <!-- E-mailadres -->
          <div>
            <input ref="refEmail" v-model="email" type="email" :placeholder="t('placeholders.email')"
                   @blur="emailTouched=true" @input="emailTouched=true"
                   :class="['w-full h-10 bg-white px-3 border text-[15px]', (emailTouched && !isValidEmail(email)) ? 'border-[#b91c1c]' : 'border-[#d8d1c6]']"/>
            <p v-if="emailTouched && !isValidEmail(email)" class="mt-1 text-[12px] text-[#b91c1c]">{{ t('messages.emailInvalid') }}</p>
          </div>

          <!-- Telefoon -->
          <div>
            <input v-model="phone" type="tel" :placeholder="t('placeholders.phone') + ' *'" class="w-full h-10 bg-white px-3 border border-[#d8d1c6] text-[15px]"/>
          </div>

          <!-- Geslacht -->
          <div class="relative">
            <select v-model="gender" @change="genderTouched=true"
                    :class="['w-full h-10 bg-white px-3 pr-8 border text-sm font-bold uppercase italic focus:ring-0 focus:border-black appearance-none', (genderTouched && !gender) ? 'border-[#b91c1c]' : 'border-black']">
              <option value="" disabled>{{ t('contact.gender.placeholder') }}</option>
              <option value="man">{{ t('contact.gender.man') }}</option>
              <option value="vrouw">{{ t('contact.gender.woman') }}</option>
            </select>
            <svg class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" fill="none"
                 stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
            <p v-if="genderTouched && !gender" class="mt-1 text-[12px] text-[#b91c1c]">{{ t('validation.chooseOption') }}</p>
          </div>

          <!-- Bericht -->
          <div>
            <textarea ref="refMessage" v-model="message" rows="4" :placeholder="t('contact.messagePlaceholder')"
                      @blur="messageTouched=true" @input="messageTouched=true"
                      :class="['w-full bg-white p-3 border text-[15px] min-h-[120px]', (messageTouched && !message) ? 'border-[#b91c1c]' : 'border-[#d8d1c6]']"></textarea>
          </div>

          <!-- Privacy -->
          <div>
            <label class="inline-flex items-start gap-3">
              <input type="checkbox" v-model="privacy" class="mt-0.5 accent-black"/>
              <span class="text-[13px] leading-relaxed text-black">{{ t('contact.privacyText') }}</span>
            </label>
            <p v-if="privacyTouched && !privacy" class="mt-1 text-[12px] text-[#b91c1c]">{{ t('validation.requiredField') }}</p>
          </div>

          <!-- Turnstile -->
          <div>
            <div v-if="turnstileEnabled" class="border border-[#e5dfd3] rounded p-2">
              <div ref="turnstile" class="mm-turnstile" />
              <p v-if="turnstileError" class="mt-2 text-[12px] text-[#b91c1c]">{{ turnstileError }}</p>
            </div>
            <div v-else class="flex items-center justify-between border border-[#e5dfd3] rounded bg-white p-2">
              <div class="flex items-center gap-2">
                <div class="w-5 h-5 grid place-items-center bg-[#55b36a] text-white text-[12px] font-bold rounded-sm">✓</div>
                <span class="text-[14px] text-[#333]">{{ t('messages.verificationDisabled') }}</span>
              </div>
              <span class="text-[10px] text-[#777]">{{ t('labels.turnstile') }}</span>
            </div>
          </div>

          <!-- Verzenden -->
          <div class="pt-1">
            <button type="submit" :disabled="!formValid || isLoading"
                    class="h-10 px-5 bg-black text-white italic uppercase font-extrabold flex items-center gap-2 disabled:opacity-60">
              <span>{{ t('buttons.sendForm') }}</span>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M5 12h14"/>
                <path d="M12 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        </form>
      </div>
        </div>
      </div>
    </div>
  </section>
  </template>

<script>
import { t as i18nT } from '../i18n'
import Turnstile from '../lib/turnstile'

export default {
  name: 'MmContact',
  props: {
    photos: { type: Array, default: () => [] },
  },
  data() {
    return {
      // Links
      // Rechts formulier
      postcode: '',
      postcodeOk: false,
      postcodeMessage: '',
      name: '',
      nameTouched: false,
      email: '',
      emailTouched: false,
      phone: '',
      gender: '',
      genderTouched: false,
      message: '',
      messageTouched: false,
      privacy: false,
      privacyTouched: false,
      // submit state
      isLoading: false,
      submitted: false,
      // Debounce validation
      postcodeTimeout: null,
      // Turnstile
      turnstileEnabled: !!(Turnstile && Turnstile.isEnabled && Turnstile.isEnabled()),
      turnstileToken: '',
      turnstileWidgetId: null,
      turnstileError: '',
    }
  },
  computed: {
    photosToShow() {
      const arr = this.photos && this.photos.length ? this.photos : [null, null, null]
      if (arr.length >= 3) return arr.slice(0,3)
      return [...arr, ...Array(3 - arr.length).fill(null)]
    },
    postcodeClass() {
      const raw = (this.postcode || '').toUpperCase().replace(/\s+/g,'')
      // Toon alleen rood als het geen cijfer-reeks is die nog aangevuld kan worden, 
      // OF als de postcodeMessage al gevuld is (wat gebeurt bij blur/submit/fout)
      const isTypingDigits = /^\d{1,4}$/.test(raw)
      const showError = raw && !this.postcodeOk && (!isTypingDigits || (this.postcodeMessage && !this.postcodeOk))
      return showError ? 'border-[#b91c1c]' : 'border-[#d8d1c6]'
    },
    formValid() {
      const emailOk = this.isValidEmail(this.email)
      const genderOk = !!this.gender
      const msgOk = !!this.message
      const nameOk = !!this.name
      const privacyOk = !!this.privacy
      const turnstileOk = (!this.turnstileEnabled) || (!!this.turnstileToken)
      const isTypingDigits = /^\d{1,4}$/.test((this.postcode || '').toUpperCase().replace(/\s+/g,''))
      const postcodeActuallyOk = this.postcodeOk && !isTypingDigits
      return !!(postcodeActuallyOk && nameOk && emailOk && genderOk && msgOk && privacyOk && turnstileOk)
    },
  },
  mounted() {
    // Focus op eerste veld
    try { this.$refs.refPostcode && this.$refs.refPostcode.focus() } catch(_) {}
    // Turnstile renderen indien geactiveerd
    if (this.turnstileEnabled) {
      Turnstile.waitForApi().then(() => {
        try {
          this.turnstileError = ''
          if (!Turnstile.isEnabled()) { this.turnstileEnabled = false; return }
          this.turnstileWidgetId = Turnstile.render(this.$refs.turnstile, {
            onToken: (token) => { this.turnstileToken = token; this.turnstileError=''; },
            onError: () => { this.turnstileError = this.t('messages.turnstileError') },
            onExpired: () => { this.turnstileToken=''; this.turnstileError=this.t('messages.turnstileExpired') },
          })
        } catch (e) {
          this.turnstileError = this.t('messages.turnstileLoadFailed')
        }
      })
    }
  },
  methods: {
    t(key, fallback){
      return i18nT(key, fallback)
    },
    isValidEmail(v) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v||'').trim())
    },
    debouncedValidatePostcode() {
      if (this.postcodeTimeout) clearTimeout(this.postcodeTimeout)
      this.postcodeTimeout = setTimeout(() => {
        this.validatePostcode()
      }, 500)
    },
    validatePostcode(force = false){
      if (this.postcodeTimeout) clearTimeout(this.postcodeTimeout)
      const raw = (this.postcode||'').toUpperCase().replace(/\s+/g,'')
      if (!raw) { this.postcodeOk=false; this.postcodeMessage=''; return; }
      const formatOk = /^\d{4}[A-Z]{2}$/.test(raw)
      if (!formatOk) {
        this.postcodeOk = false
        if (!force && /^\d{0,4}$/.test(raw)) {
          this.postcodeMessage = '' // Stil laten typen
        } else {
          this.postcodeMessage = this.t('messages.postcodeInvalidFormat')
        }
        return
      }
      // Eventueel uitbreiden met backend validatie per regio; nu simpel ok
      this.postcodeOk = true
      this.postcodeMessage = this.t('messages.postcodeValid')
    },
    handleSubmit(){
      // markeer touched
      this.nameTouched = true
      this.emailTouched = true
      this.genderTouched = true
      this.messageTouched = true
      this.privacyTouched = true
      this.validatePostcode(true)

      if (!this.formValid) {
        // focus eerste fout
        if (!this.postcodeOk) return this.$refs.refPostcode && this.$refs.refPostcode.focus()
        if (!this.name) return this.$refs.refName && this.$refs.refName.focus()
        if (!this.isValidEmail(this.email)) return this.$refs.refEmail && this.$refs.refEmail.focus()
        if (!this.message) return this.$refs.refMessage && this.$refs.refMessage.focus()
        return
      }
      this.isLoading = true
      // In deze implementatie geen backend: simuleer succesvolle submit
      setTimeout(() => {
        this.isLoading = false
        this.submitted = true
        try { Turnstile.reset(this.turnstileWidgetId) } catch(_) {}
      }, 400)
    }
  }
}
</script>

<style scoped>
</style>
